import React, { useRef, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';

const GameDeveloper = () => {
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [gameState, setGameState] = useState('START'); // START, PLAYING, GAME_OVER
  
  useEffect(() => {
    if (gameState !== 'PLAYING') return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    // Config
    const width = canvas.width;
    const height = canvas.height;
    
    // Game Objects
    let player = {
      x: width / 2,
      y: height / 2,
      angle: 0,
      radius: 12,
      vX: 0,
      vY: 0
    };
    
    let bullets = [];
    let enemies = [];
    let particles = [];
    let frames = 0;
    let currentScore = 0;
    
    // Keys
    const keys = { w: false, a: false, s: false, d: false, ArrowUp: false, ArrowLeft: false, ArrowRight: false, ArrowDown: false, Space: false };
    
    const onKeyDown = (e) => { 
      if(e.code === 'Space') keys.Space = true;
      else keys[e.key] = true; 
    };
    const onKeyUp = (e) => { 
      if(e.code === 'Space') { keys.Space = false; player.canShoot = true; }
      else keys[e.key] = false; 
    };
    
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    
    player.canShoot = true;

    const spawnEnemy = () => {
      const edge = Math.floor(Math.random() * 4);
      let x, y;
      if (edge === 0) { x = Math.random() * width; y = -30; } // Top
      else if (edge === 1) { x = width + 30; y = Math.random() * height; } // Right
      else if (edge === 2) { x = Math.random() * width; y = height + 30; } // Bottom
      else { x = -30; y = Math.random() * height; } // Left
      
      enemies.push({
        x, y,
        radius: Math.random() * 10 + 10,
        speed: Math.random() * 1 + 0.5 + (frames / 3000), // Gets faster over time
        hp: 1
      });
    };

    const drawPlayer = () => {
      ctx.save();
      ctx.translate(player.x, player.y);
      ctx.rotate(player.angle);
      
      ctx.strokeStyle = '#00ff41';
      ctx.lineWidth = 2;
      ctx.beginPath();
      // Draw a sleek ship (triangle)
      ctx.moveTo(player.radius, 0);
      ctx.lineTo(-player.radius, player.radius * 0.8);
      ctx.lineTo(-player.radius * 0.5, 0);
      ctx.lineTo(-player.radius, -player.radius * 0.8);
      ctx.closePath();
      ctx.stroke();
      
      // Thrust fire
      if (keys.w || keys.ArrowUp) {
        ctx.strokeStyle = '#ff003c';
        ctx.beginPath();
        ctx.moveTo(-player.radius * 0.5, 0);
        ctx.lineTo(-player.radius * 1.5, Math.random() * 4 - 2);
        ctx.stroke();
      }
      
      ctx.restore();
    };
    
    let animationId;
    const update = () => {
      ctx.clearRect(0, 0, width, height);
      frames++;
      
      // Controls
      if (keys.a || keys.ArrowLeft) player.angle -= 0.1;
      if (keys.d || keys.ArrowRight) player.angle += 0.1;
      if (keys.w || keys.ArrowUp) {
        player.vX += Math.cos(player.angle) * 0.15;
        player.vY += Math.sin(player.angle) * 0.15;
      }
      
      // Friction
      player.vX *= 0.98;
      player.vY *= 0.98;
      
      player.x += player.vX;
      player.y += player.vY;
      
      // Wrap around screen
      if (player.x < 0) player.x = width;
      if (player.x > width) player.x = 0;
      if (player.y < 0) player.y = height;
      if (player.y > height) player.y = 0;
      
      // Shoot
      if (keys.Space && player.canShoot) {
        bullets.push({
          x: player.x + Math.cos(player.angle) * player.radius,
          y: player.y + Math.sin(player.angle) * player.radius,
          vX: Math.cos(player.angle) * 7,
          vY: Math.sin(player.angle) * 7,
          life: 100
        });
        player.canShoot = false;
        // Cool down could be added, but manual tapping is fine.
      }
      
      // Update Bullets
      ctx.fillStyle = '#00ffff';
      for (let i = bullets.length - 1; i >= 0; i--) {
        let b = bullets[i];
        b.x += b.vX;
        b.y += b.vY;
        b.life--;
        
        ctx.beginPath();
        ctx.rect(b.x, b.y, 3, 3);
        ctx.fill();
        
        if (b.life <= 0) bullets.splice(i, 1);
      }
      
      // Spawn Enemies
      let spawnRate = Math.max(20, 100 - Math.floor(frames / 100)); // Gets harder
      if (frames % spawnRate === 0) spawnEnemy();
      
      // Update Enemies
      for (let i = enemies.length - 1; i >= 0; i--) {
        let e = enemies[i];
        
        // Move towards player
        let dx = player.x - e.x;
        let dy = player.y - e.y;
        let dist = Math.sqrt(dx*dx + dy*dy);
        e.x += (dx/dist) * e.speed;
        e.y += (dy/dist) * e.speed;
        
        ctx.strokeStyle = '#ff003c';
        ctx.beginPath();
        // Draw hex or square for malware
        ctx.rect(e.x - e.radius/2, e.y - e.radius/2, e.radius, e.radius);
        ctx.stroke();
        
        // Collision with player
        if (dist < player.radius + e.radius/2) {
          setGameState('GAME_OVER');
          return; // Stop game loop immediately
        }
        
        // Collision with bullets
        for (let j = bullets.length - 1; j >= 0; j--) {
          let b = bullets[j];
          let bdx = b.x - e.x;
          let bdy = b.y - e.y;
          if (Math.sqrt(bdx*bdx + bdy*bdy) < e.radius) {
            // Hit!
            bullets.splice(j, 1);
            enemies.splice(i, 1);
            currentScore += 10;
            setScore(currentScore);
            
            // Particles
            for(let p=0; p<5; p++) {
              particles.push({
                x: e.x, y: e.y,
                vX: (Math.random() - 0.5) * 4,
                vY: (Math.random() - 0.5) * 4,
                life: 30
              });
            }
            break; // Break bullet loop to prevent double collision
          }
        }
      }
      
      // Update Particles
      ctx.fillStyle = '#ff003c';
      for (let i = particles.length - 1; i >= 0; i--) {
        let p = particles[i];
        p.x += p.vX;
        p.y += p.vY;
        p.life--;
        ctx.globalAlpha = p.life / 30;
        ctx.fillRect(p.x, p.y, 2, 2);
        if (p.life <= 0) particles.splice(i, 1);
      }
      ctx.globalAlpha = 1.0;
      
      drawPlayer();
      
      animationId = requestAnimationFrame(update);
    };
    
    update();
    
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      cancelAnimationFrame(animationId);
    };
  }, [gameState]);

  return (
    <div className="container" style={{ textAlign: 'center' }}>
      <Helmet>
        <title>Ariel Rotem | Game Developer & Programmer</title>
        <meta name="description" content="Play Data Defender, an HTML5 Canvas game developed by Ariel Rotem." />
      </Helmet>
      <div className="mb-4">
        <h1 className="glitch" style={{ color: 'var(--accent-secondary)', fontSize: '2.5rem' }}>$ ./play_game.sh</h1>
      </div>

      <div className="terminal-card" style={{ maxWidth: '800px', margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <h3 className="mb-2 text-primary">&gt; DATA_DEFENDER.exe</h3>
        <p className="mono text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
          Defend the core. A simple demonstration to make up for the old, un-winnable maze.
        </p>

        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', maxWidth: '640px', marginBottom: '10px' }}>
          <span className="mono" style={{ color: 'var(--accent-secondary)' }}>SCORE: {score}</span>
          <span className="mono" style={{ color: 'var(--text-secondary)' }}>[W/A/S/D] Move | [SPACE] Shoot</span>
        </div>
        
        <div style={{ position: 'relative', width: '100%', maxWidth: '640px', display: 'flex', justifyContent: 'center' }}>
          <canvas 
            ref={canvasRef} 
            width={640} 
            height={480} 
            style={{ 
              border: '2px solid rgba(0, 255, 65, 0.3)', 
              backgroundColor: '#050505',
              boxShadow: '0 0 20px rgba(0, 255, 65, 0.1)',
              maxWidth: '100%'
            }} 
          />
          
          {gameState === 'START' && (
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
              <button 
                className="btn" 
                onClick={() => { setGameState('PLAYING'); setScore(0); }}
                style={{ fontSize: '1.5rem', padding: '15px 30px' }}
              >
                INITIALIZE
              </button>
            </div>
          )}

          {gameState === 'GAME_OVER' && (
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', backgroundColor: 'rgba(0,0,0,0.8)', padding: '20px', border: '1px solid var(--accent-tertiary)' }}>
              <h2 className="mono" style={{ color: 'var(--accent-tertiary)' }}>SYSTEM COMPROMISED</h2>
              <p className="mono mb-2">Final Score: {score}</p>
              <button 
                className="btn" 
                onClick={() => { setGameState('PLAYING'); setScore(0); }}
                style={{ borderColor: 'var(--accent-tertiary)', color: 'var(--accent-tertiary)' }}
              >
                REBOOT
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GameDeveloper;
